/*
 * Bioroot shared components (Dawn rebuild).
 * Lean replacements for the Shrine theme components used across sections.
 * Element names and data-* attributes match the original markup so ported
 * sections keep working unchanged. Loaded once, deferred, on every page.
 */
(() => {
  const define = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };

  /* <copy-button data-content="CODE"> — copies text, sets data-success for 3s */
  define(
    'copy-button',
    class extends HTMLElement {
      connectedCallback() {
        if (this._bound) return;
        this._bound = true;
        this.addEventListener('click', () => this.copy());
      }
      async copy() {
        const text = (this.dataset.content || '').trim();
        try {
          await navigator.clipboard.writeText(text);
        } catch (e) {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.className = 'visually-hidden';
          this.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          ta.remove();
        }
        this.dataset.success = 'true';
        clearTimeout(this._t);
        this._t = setTimeout(() => (this.dataset.success = 'false'), 3000);
      }
    }
  );

  /* <countdown-timer data-duration="seconds" data-auto-play="true"> — mm:ss, restarts at 90s like Shrine */
  define(
    'countdown-timer',
    class extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;
        this.textContent = '';
        this.min = document.createElement('span');
        this.sec = document.createElement('span');
        this.append(this.min, document.createTextNode(':'), this.sec);
        this.tick();
        if (this.dataset.autoPlay === 'true') this.play();
      }
      disconnectedCallback() {
        clearInterval(this._i);
        this._init = false;
      }
      pad(n) {
        return String(n).padStart(2, '0');
      }
      tick() {
        let t = parseInt(this.dataset.duration, 10);
        if (!t || t <= 0) t = 90;
        this.min.textContent = this.pad(Math.floor(t / 60));
        this.sec.textContent = this.pad(t % 60);
        this.dataset.duration = t - 1;
      }
      play() {
        if (this._i) return;
        this._i = setInterval(() => this.tick(), 1000);
      }
      pause() {
        clearInterval(this._i);
        this._i = null;
      }
    }
  );

  /* <internal-video data-autoplay> — play/pause, mute toggle, seekable timeline, lazy sources */
  define(
    'internal-video',
    class extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;
        this.video = this.querySelector('video');
        if (!this.video) return;
        this.playBtn = this.querySelector('.internal-video__play');
        this.soundBtn = this.querySelector('.internal-video__sound-btn');
        this.timeline = this.querySelector('.internal-video__timeline');
        this.playBtn?.addEventListener('click', () => this.toggle());
        this.soundBtn?.addEventListener('click', () => this.toggleSound());
        const v = this.video;
        v.addEventListener('ended', () => this.classList.remove('internal-video--playing'));
        v.addEventListener('waiting', () => this.classList.add('internal-video--loading'));
        ['canplaythrough', 'play'].forEach((e) => v.addEventListener(e, () => this.classList.remove('internal-video--loading')));
        if (this.timeline) {
          v.addEventListener('timeupdate', () =>
            this.style.setProperty('--completed', ((v.currentTime / v.duration) * 100 || 0) + '%')
          );
          const seek = (e) => {
            const p = e.touches ? e.touches[0] : e;
            const r = this.timeline.getBoundingClientRect();
            v.currentTime = Math.min(Math.max((p.clientX - r.left) / r.width, 0), 1) * v.duration;
          };
          let dragging = false;
          const start = (e) => {
            e.preventDefault();
            dragging = true;
            seek(e);
          };
          this.timeline.addEventListener('mousedown', start);
          this.timeline.addEventListener('touchstart', start, { passive: false });
          const move = (e) => dragging && seek(e);
          const stop = () => (dragging = false);
          document.addEventListener('mousemove', move);
          document.addEventListener('touchmove', move, { passive: true });
          document.addEventListener('mouseup', stop);
          document.addEventListener('touchend', stop);
        }
        if (this.dataset.autoplay === 'true') {
          this.io = new IntersectionObserver(
            (entries) => {
              if (!entries.some((en) => en.isIntersecting)) return;
              this.loadSources();
              v.play().catch(() => {});
              this.io.disconnect();
            },
            { threshold: 0.05 }
          );
          this.io.observe(this);
        }
      }
      loadSources() {
        let changed = false;
        this.video.querySelectorAll('source[data-src]').forEach((s) => {
          s.src = s.dataset.src;
          s.removeAttribute('data-src');
          changed = true;
        });
        if (changed) this.video.load();
      }
      toggle() {
        const v = this.video;
        if (v.paused) {
          this.loadSources();
          v.play().catch(() => {});
          this.classList.add('internal-video--playing');
        } else {
          v.pause();
          this.classList.remove('internal-video--playing');
        }
      }
      toggleSound() {
        this.video.muted = !this.video.muted;
        this.classList.toggle('internal-video--muted', this.video.muted);
      }
    }
  );

  /*
   * <splide-component> — lightweight CSS scroll-snap slider replacing the Splide library.
   * Keeps Shrine's markup: .splide > .splide__track > .splide__list > .splide__slide
   * Reads the same data-* options (slides-desktop/mobile, gap-desktop/mobile, arrows,
   * pagination, autoplay, autoplay-speed, type, destroy-desktop/mobile, padding).
   * When a breakpoint is "destroyed" (slider off) or all slides fit, it renders as a static row.
   */
  define(
    'splide-component',
    class extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;
        this.root = this.querySelector('.splide') || this;
        this.track = this.root.querySelector('.splide__track');
        this.list = this.root.querySelector('.splide__list');
        if (!this.track || !this.list) return;
        this.slides = Array.from(this.list.children).filter((el) => el.classList.contains('splide__slide'));
        this.mq = window.matchMedia('(max-width: 749px)');
        this.apply = this.apply.bind(this);
        this.mq.addEventListener('change', this.apply);
        this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
        this.apply();
      }
      disconnectedCallback() {
        this.mq?.removeEventListener('change', this.apply);
        clearInterval(this._auto);
        this._init = false;
      }
      opt(name, fallback) {
        const v = this.dataset[name];
        return v === undefined || v === '' ? fallback : v;
      }
      apply() {
        const mobile = this.mq.matches;
        const d = this.dataset;
        const per = parseInt(mobile ? this.opt('slidesMobile', 1) : this.opt('slidesDesktop', 3), 10) || 1;
        const gap = parseInt(mobile ? this.opt('gapMobile', 15) : this.opt('gapDesktop', 30), 10) || 0;
        const padR = parseInt(mobile ? this.opt('sidePaddingMobile', 0) : this.opt('sidePaddingDesktop', 0), 10) || 0;
        const destroyed = (mobile ? d.destroyMobile : d.destroyDesktop) === 'true';
        const fits = this.slides.length <= per;
        this.static = destroyed || fits;
        this.fade = this.opt('type', 'slide') === 'fade';
        this.per = per;
        this.style.setProperty('--br-per', per);
        this.style.setProperty('--br-gap', gap + 'px');
        this.style.setProperty('--br-pad', padR + 'px');
        this.classList.add('br-slider');
        this.classList.toggle('br-slider--static', this.static);
        this.classList.toggle('br-slider--fade', this.fade && !this.static);
        this.root.classList.add('is-initialized', 'is-rendered');
        this.buildControls();
        this.setActive(0);
        clearInterval(this._auto);
        if (!this.static && d.autoplay === 'true') {
          const ms = (parseInt(d.autoplaySpeed, 10) || 5) * 1000;
          this._auto = setInterval(() => {
            if (this.matches(':hover')) return;
            this.go(this.index + 1 >= this.pages ? 0 : this.index + 1);
          }, ms);
        }
      }
      get pages() {
        return this.static ? 1 : this.fade ? this.slides.length : Math.max(1, this.slides.length - this.per + 1);
      }
      buildControls() {
        this.root.querySelectorAll('.br-slider__arrows, .splide__pagination').forEach((n) => n.remove());
        if (this.static) return;
        const d = this.dataset;
        if (d.arrows !== 'false') {
          const wrap = document.createElement('div');
          wrap.className = 'br-slider__arrows splide__arrows';
          const color = d.arrowsColor ? ` color-${d.arrowsColor}` : '';
          wrap.innerHTML =
            `<button type="button" class="splide__arrow splide__arrow--prev${color}" aria-label="Previous slide"><svg viewBox="0 0 40 40" width="40" height="40" aria-hidden="true"><path d="m15.5 0.932-4.3 4.38 14.5 14.6-14.5 14.5 4.3 4.4 14.6-14.6 4.4-4.3-4.4-4.4-14.6-14.6z"></path></svg></button>` +
            `<button type="button" class="splide__arrow splide__arrow--next${color}" aria-label="Next slide"><svg viewBox="0 0 40 40" width="40" height="40" aria-hidden="true"><path d="m15.5 0.932-4.3 4.38 14.5 14.6-14.5 14.5 4.3 4.4 14.6-14.6 4.4-4.3-4.4-4.4-14.6-14.6z"></path></svg></button>`;
          wrap.children[0].addEventListener('click', () => this.go(this.index - 1));
          wrap.children[1].addEventListener('click', () => this.go(this.index + 1));
          this.root.appendChild(wrap);
          this.arrows = wrap;
        }
        if (d.pagination !== 'false' && this.pages > 1) {
          const ul = document.createElement('ul');
          ul.className = 'splide__pagination';
          const dots = d.dotsColor ? ` color-${d.dotsColor} dots-custom-color` : '';
          for (let i = 0; i < this.pages; i++) {
            const li = document.createElement('li');
            li.innerHTML = `<button type="button" class="splide__pagination__page${dots}" aria-label="Go to slide ${i + 1}"></button>`;
            li.firstChild.addEventListener('click', () => this.go(i));
            ul.appendChild(li);
          }
          this.root.appendChild(ul);
          this.dots = ul;
        }
      }
      go(i) {
        const n = this.pages;
        if (this.opt('type', 'slide') === 'loop') i = (i + n) % n;
        i = Math.max(0, Math.min(i, n - 1));
        if (this.fade) return this.setActive(i);
        const slide = this.slides[i];
        if (slide) this.track.scrollTo({ left: slide.offsetLeft - this.list.offsetLeft, behavior: 'smooth' });
        this.setActive(i);
      }
      onScroll() {
        if (this.static || this.fade) return;
        clearTimeout(this._s);
        this._s = setTimeout(() => {
          const x = this.track.scrollLeft;
          let best = 0;
          this.slides.forEach((s, i) => {
            if (Math.abs(s.offsetLeft - this.list.offsetLeft - x) < Math.abs(this.slides[best].offsetLeft - this.list.offsetLeft - x)) best = i;
          });
          this.setActive(Math.min(best, this.pages - 1), true);
        }, 60);
      }
      setActive(i) {
        this.index = i;
        this.slides.forEach((s, k) => {
          const on = this.fade ? k === i : k >= i && k < i + this.per;
          s.classList.toggle('is-active', k === i);
          s.classList.toggle('is-visible', on);
          s.classList.toggle('is-instant-active', k === i);
        });
        this.dots?.querySelectorAll('button').forEach((b, k) => {
          b.classList.toggle('is-active', k === i);
          b.setAttribute('aria-current', k === i ? 'true' : 'false');
        });
        if (this.arrows && this.opt('type', 'slide') !== 'loop') {
          this.arrows.children[0].disabled = i <= 0;
          this.arrows.children[1].disabled = i >= this.pages - 1;
        }
        const active = this.slides[i];
        if (active) this.style.setProperty('--active-slide-height', active.offsetHeight + 'px');
      }
    }
  );

  /* Shrine-style section load animations (.animate-section -> .animate--shown) */
  const anim = document.documentElement.dataset.loadAnimations;
  if (anim === 'true') {
    const repeat = document.documentElement.dataset.repeatAnimations === 'true';
    let bottom = 50;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('animate--shown');
          else if (repeat) e.target.classList.remove('animate--shown');
        }),
      { rootMargin: `0px 0px -${bottom}px 0px` }
    );
    const init = (root = document, instant = false) =>
      root.querySelectorAll('.animate-section').forEach((el) => (instant ? el.classList.add('animate--shown') : io.observe(el)));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
    else init();
    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:load', (e) => init(e.target, true));
      document.addEventListener('shopify:section:reorder', () => init(document, true));
    }
  }

  /* Section-level "Add to cart" buttons (id^=SectionAtcBtn-) submit the main product form */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[id^="SectionAtcBtn-"]');
    if (!btn) return;
    const form = document.querySelector('product-form[data-main="true"]') || document.querySelector('product-form');
    if (form && typeof form.onSubmitHandler === 'function') {
      e.preventDefault();
      const realForm = form.querySelector('form');
      form.onSubmitHandler(new SubmitEvent('submit', { submitter: realForm?.querySelector('[type="submit"]') }));
    }
  });

  /* External links open in a new tab (kept from live theme) */
  const externalLinks = () => {
    for (const a of document.links) {
      if (a.hostname && a.hostname !== location.hostname) {
        a.target = '_blank';
        a.rel = 'noreferrer noopener';
      }
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', externalLinks);
  else externalLinks();
})();
